import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  House, Storefront, Package, ShoppingCart, UsersThree, Truck, ChartBar,
  ArrowsLeftRight, SignOut, Diamond, List, X, Gear, ClockClockwise,
  ShoppingBag, Coins, ArrowsClockwise, Wrench, IdentificationCard,
  MoonStars, Sun, Sliders, FileText, Hammer, Scales, Radio, Calculator, Receipt,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  {
    label: "Operate",
    items: [
      { to: "/", label: "Dashboard", icon: House, end: true },
      { to: "/pos", label: "POS Billing", icon: Storefront },
      { to: "/sales", label: "Sales", icon: Receipt },
      { to: "/orders", label: "Custom Orders", icon: ShoppingBag },
      { to: "/repairs", label: "Repairs", icon: Wrench },
      { to: "/manufacturing", label: "Manufacturing", icon: Hammer },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/inventory", label: "Products", icon: Package },
      { to: "/purchases", label: "Purchases", icon: ShoppingCart },
      { to: "/transfers", label: "Branch Transfers", icon: ArrowsClockwise },
      { to: "/old-gold", label: "Old Gold", icon: ArrowsLeftRight },
      { to: "/metal-ledger", label: "Metal Ledger", icon: Scales },
      { to: "/rfid", label: "RFID", icon: Radio },
    ],
  },
  {
    label: "Customers",
    items: [
      { to: "/customers", label: "Customers", icon: UsersThree },
      { to: "/schemes", label: "Gold Schemes", icon: Coins },
      { to: "/suppliers", label: "Suppliers", icon: Truck },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/reports", label: "Reports", icon: ChartBar },
      { to: "/accounting", label: "Accounting", icon: Calculator },
      { to: "/gst", label: "GST Returns", icon: FileText },
      { to: "/activity", label: "Activity Log", icon: ClockClockwise },
    ],
  },
  {
    label: "Configure",
    items: [
      { to: "/masters", label: "Masters", icon: Gear },
      { to: "/users", label: "Users & Roles", icon: IdentificationCard },
      { to: "/settings", label: "Settings", icon: Sliders },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const role = user?.role || "cashier";
  const isAdmin = role === "owner" || role === "super_admin";
  // Allowed slugs per role (mirror of backend ROLE_MENU_ACCESS)
  const ALLOWED = {
    super_admin: null, owner: null,  // null => everything
    manager: ["", "pos", "sales", "orders", "repairs", "manufacturing", "inventory", "purchases", "transfers", "old-gold", "metal-ledger", "rfid", "customers", "schemes", "suppliers", "reports", "accounting", "gst", "activity", "masters"],
    cashier: ["", "pos", "sales", "customers", "old-gold"],
    accountant: ["", "sales", "purchases", "customers", "suppliers", "reports", "accounting", "gst"],
    karigar: ["", "manufacturing"],
    sales: ["", "pos", "sales", "customers", "orders", "repairs", "schemes"],
  };
  const allowedSet = ALLOWED[role] === null ? null : new Set(ALLOWED[role] || []);
  const isAllowed = (to) => {
    if (!allowedSet) return true;
    const slug = to.replace(/^\/+/, "");
    return allowedSet.has(slug);
  };

  const initials = (user?.name || "?")
    .split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 border-b border-border bg-background/80 backdrop-blur-xl flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <Diamond size={20} weight="fill" className="text-gold" />
          <span className="font-display text-lg tracking-tight">AURUM</span>
        </Link>
        <button
          data-testid="mobile-menu-toggle"
          className="p-2 rounded-md hover:bg-accent"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X size={20} /> : <List size={20} />}
        </button>
      </header>

      <aside
        data-testid="app-sidebar"
        className={`fixed md:sticky top-0 z-30 h-screen w-64 shrink-0 border-r border-border bg-card/40 backdrop-blur-xl flex flex-col transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="h-16 px-6 flex items-center gap-2 border-b border-border shrink-0">
          <div className="h-9 w-9 rounded-lg bg-primary/10 grid place-items-center border border-primary/30">
            <Diamond size={18} weight="fill" className="text-gold" />
          </div>
          <div>
            <div className="font-display text-lg leading-none tracking-tight">AURUM</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-0.5">Jewellery ERP</div>
          </div>
          <button
            data-testid="theme-toggle-side"
            onClick={toggle}
            className="ml-auto p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
            title="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} /> : <MoonStars size={16} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {NAV_GROUPS.map((g) => {
            const visibleItems = g.items.filter((i) => isAllowed(i.to));
            if (visibleItems.length === 0) return null;
            return (
              <div key={g.label}>
                <div className="px-3 mb-1 text-[9px] uppercase tracking-[0.25em] text-muted-foreground/70">{g.label}</div>
                <div className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-accent text-foreground sidebar-item-active"
                            : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                        }`
                      }
                    >
                      <item.icon size={16} weight="duotone" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border p-3 shrink-0">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-gold font-semibold text-xs">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
              title="Logout"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <button
          aria-label="Close sidebar"
          className="md:hidden fixed inset-0 z-20 bg-black/60"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main className="flex-1 min-w-0 pt-14 md:pt-0">{children}</main>
    </div>
  );
}
